import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { LoadingButton } from '@mui/lab'
import { Grid, InputAdornment } from '@mui/material'
import { IAccountType } from '@procom-labs/common'
import {
  PhoneInput,
  RadioGroupHookInput,
  RadioGroupInputOption,
  RadioGroupValueType,
  TextHookField,
} from '@procom-labs/molecules'

import { Header } from '@auth-portal/components/header'
import { useLoginTypes } from '@auth-portal/hooks'
import { LandingFormValues } from '@auth-portal/types'

const AccountTypeOptions: RadioGroupInputOption[] = [
  {
    label: 'form.createAccount.client',
    value: IAccountType.Client,
  },
  {
    label: 'form.createAccount.contractor',
    value: IAccountType.Contractor,
  },
]

export const ProfileSelectionForm: React.FC<{ isLoading: boolean }> = ({
  isLoading,
}) => {
  const { t } = useTranslation('main')

  const {
    control,
    watch,
    formState: { isSubmitting },
  } = useFormContext<LandingFormValues>()

  const { isClientLogin } = useLoginTypes()

  const accountType = watch('accountType')

  return (
    <Grid
      display="flex"
      flexDirection="column"
      alignItems="center"
      rowGap={2}
      sx={{
        px: {
          xs: 1,
          sm: 4,
          lg: 5,
          xl: 7,
          k2: 12.5,
        },
      }}
    >
      <Header
        heading={
          !isClientLogin
            ? t('form.createAccount.headings.title2')
            : t('form.createAccount.multiSelectHeading')
        }
      />

      {isClientLogin && (
        <RadioGroupHookInput
          control={control}
          row
          name="accountType"
          valueType={RadioGroupValueType.String}
          options={AccountTypeOptions}
        />
      )}

      {accountType && (
        <>
          <TextHookField
            control={control}
            name="firstName"
            type="text"
            label={t('form.createAccount.firstName')}
            customLabel={false}
            InputLabelProps={{
              shrink: false,
            }}
            required
          />
          <TextHookField
            control={control}
            name="lastName"
            type="text"
            label={t('form.createAccount.lastName')}
            customLabel={false}
            InputLabelProps={{
              shrink: false,
            }}
            required
          />
          {accountType === IAccountType.Client && (
            <>
              <TextHookField
                control={control}
                name="phoneNumber"
                label={t('form.createAccount.phoneNumber')}
                customLabel={false}
                InputProps={{
                  inputComponent: PhoneInput,
                  startAdornment: (
                    <InputAdornment position="start">+1</InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  shrink: false,
                }}
                required
              />
              <TextHookField
                control={control}
                name="companyName"
                type="text"
                label={t('form.createAccount.companyLegalName')}
                customLabel={false}
                InputLabelProps={{
                  shrink: false,
                }}
                required
              />
            </>
          )}
        </>
      )}

      <LoadingButton
        size="large"
        color="secondary"
        variant="contained"
        type="submit"
        fullWidth
        sx={{ mt: 1 }}
        disabled={!accountType}
        loading={isLoading || isSubmitting}
      >
        {t('common.btn.continue')}
      </LoadingButton>
    </Grid>
  )
}
